
import { DiaryEntry } from "@/utils/diary/types";
import { useState } from "react";
import { ChevronLeft, ChevronRight, Maximize, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface EntryImageGalleryProps {
  entry: DiaryEntry;
}

const EntryImageGallery = ({ entry }: EntryImageGalleryProps) => {
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { language } = useLanguage();
  
  // If no images or empty array, don't render anything
  if (!entry.images || entry.images.length === 0) {
    return null;
  }
  
  const showFullscreen = (imageUrl: string, index: number) => {
    setFullscreenImage(imageUrl);
    setCurrentIndex(index);
  };
  
  const closeFullscreen = () => {
    setFullscreenImage(null);
  };
  
  const nextImage = () => {
    if (!entry.images) return;
    setCurrentIndex((prev) => (prev + 1) % entry.images!.length);
    setFullscreenImage(entry.images[(currentIndex + 1) % entry.images.length]);
  };
  
  const prevImage = () => {
    if (!entry.images) return;
    setCurrentIndex((prev) => (prev - 1 + entry.images!.length) % entry.images!.length);
    setFullscreenImage(entry.images[(currentIndex - 1 + entry.images.length) % entry.images.length]);
  };
  
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {entry.images.slice(0, 3).map((imageUrl, index) => (
          <div 
            key={index} 
            className="relative rounded-md overflow-hidden cursor-pointer group"
            onClick={() => showFullscreen(imageUrl, index)}
          >
            <img 
              src={imageUrl} 
              alt={`Entry image ${index + 1}`} 
              className="h-24 object-cover w-full"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
              <Maximize className="text-white opacity-0 group-hover:opacity-100 h-5 w-5" />
            </div>
          </div>
        ))}
        
        {entry.images.length > 3 && (
          <div 
            className="relative rounded-md overflow-hidden cursor-pointer flex items-center justify-center bg-muted h-24"
            onClick={() => showFullscreen(entry.images![3], 3)}
          >
            <span className="text-muted-foreground font-medium">+{entry.images.length - 3} {t('gallery.more', language) || 'more'}</span>
          </div>
        )}
      </div>
      
      <Dialog open={!!fullscreenImage} onOpenChange={(open) => !open && closeFullscreen()}>
        <DialogContent className="sm:max-w-4xl p-0 bg-transparent border-none shadow-none">
          <div className="relative">
            {fullscreenImage && (
              <img 
                src={fullscreenImage} 
                alt={t('gallery.entryImageFullscreen', language) || 'Entry image fullscreen'} 
                className="max-h-[80vh] mx-auto rounded-md"
              />
            )}
            
            <Button 
              variant="secondary" 
              size="icon" 
              className="absolute top-2 right-2 bg-background/80"
              onClick={closeFullscreen}
            >
              <X className="h-4 w-4" />
            </Button>
            
            {entry.images && entry.images.length > 1 && (
              <>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-background/80 px-2 py-1 rounded-full text-xs">
                  {currentIndex + 1} / {entry.images.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EntryImageGallery;
